import { useCallback, useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Spinner,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { getMyEngagementHistory } from "../../services/resourceAllocation.service";
import { formatResourceAllocationDuration } from "../../utils/resourceAllocationDuration";
import type { IMyEngagementMonthApi } from "../../services/types/resourceAllocation";

interface EngagementHistoryDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

const useStyles = makeStyles({
  surface: {
    maxWidth: "560px",
    width: "100%",
  },
  info: {
    marginBottom: "1rem",
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  monthBlock: {
    marginBottom: "1.25rem",
  },
  monthHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    fontWeight: tokens.fontWeightSemibold,
    borderBottomWidth: "1px",
    borderBottomStyle: "solid",
    borderBottomColor: tokens.colorNeutralStroke2,
    paddingBottom: "0.25rem",
    marginBottom: "0.5rem",
  },
  line: {
    display: "flex",
    justifyContent: "space-between",
    columnGap: "1rem",
    paddingBlock: "0.15rem",
  },
  lineMeta: {
    display: "flex",
    flexDirection: "column",
  },
  projectName: {
    fontWeight: tokens.fontWeightRegular,
  },
  managerName: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  lineHours: {
    whiteSpace: "nowrap",
    fontWeight: tokens.fontWeightSemibold,
  },
  empty: {
    color: tokens.colorNeutralForeground3,
    paddingBlock: "1rem",
    textAlign: "center",
  },
  centered: {
    display: "flex",
    justifyContent: "center",
    paddingBlock: "2rem",
  },
});

function formatMonthYear(year: number, month: number): string {
  return `${String(month).padStart(2, "0")}.${year}`;
}

const EngagementHistoryDialog: React.FC<EngagementHistoryDialogProps> = ({
  open,
  setOpen,
}) => {
  const styles = useStyles();
  const [months, setMonths] = useState<IMyEngagementMonthApi[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchHistory = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getMyEngagementHistory();
      setMonths(data.months);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchHistory();
    }
  }, [open, fetchHistory]);

  return (
    <Dialog open={open} onOpenChange={(_, data) => setOpen(data.open)}>
      <DialogSurface className={styles.surface}>
        <DialogBody>
          <DialogTitle>Historia zaangażowania</DialogTitle>
          <DialogContent>
            <div className={styles.info}>
              Dane o zleconych godzinach dostępne są od 07.2026.
            </div>

            {isLoading ? (
              <div className={styles.centered}>
                <Spinner label="Ładowanie historii..." />
              </div>
            ) : months.length === 0 ? (
              <div className={styles.empty}>
                Brak zleconych godzin w tym okresie.
              </div>
            ) : (
              months.map((m) => (
                <div key={`${m.year}-${m.month}`} className={styles.monthBlock}>
                  <div className={styles.monthHeader}>
                    <span>{formatMonthYear(m.year, m.month)}</span>
                    <span>{formatResourceAllocationDuration(m.totalOrderedMinutes)}</span>
                  </div>
                  {m.lines.map((line, index) => (
                    <div
                      key={`${line.youTrackProjectId}-${line.managerFullName}-${index}`}
                      className={styles.line}
                    >
                      <span className={styles.lineMeta}>
                        <span className={styles.projectName}>
                          {line.youTrackProjectLabel}
                        </span>
                        <span className={styles.managerName}>
                          {line.managerFullName}
                        </span>
                      </span>
                      <span className={styles.lineHours}>
                        {formatResourceAllocationDuration(line.orderedMinutes)}
                      </span>
                    </div>
                  ))}
                </div>
              ))
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={() => setOpen(false)}>
              Zamknij
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default EngagementHistoryDialog;
